import { makeObservable, observable } from 'mobx';
import type { RetentionPolicy, RetentionType } from 'api/shared/retentionPolicy';

const DEFAULT_STATUS = 'all';
const DEFAULT_RETENTION_TYPE: RetentionType = 'days_to_keep';
const DEFAULT_VALUE = 30;

/**
 * The slice of an owning `Aggregate` this child actually uses — it only ever
 * self-removes. Depending on this instead of `Aggregate<P, …>` keeps the shared
 * policy decoupled from the owner's concrete type: `Aggregate` is **invariant**
 * in its parent type `P` (its child-factory places `P` in a contravariant
 * position), so no single `Aggregate<P, …>` is one that every owner's aggregate
 * (`Aggregate<EditableNodeDefinition, …>`, `Aggregate<EditableZone, …>`) assigns
 * to. Any of them structurally satisfies this interface.
 */
interface RetentionPolicyOwner {
  remove(policy: EditableRetentionPolicy): void;
}

// Monotonic source of stable per-instance keys for React list rendering. New policies have no server id and array index is
// unstable across add/remove, so each instance gets a unique key at construction that never changes.
let nextRetentionPolicyKey = 0;

/**
 * One retention policy, as a reactive child of an owning editable entity (a node
 * definition, a zone, …). The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it
 * and never reads the parent, so the aggregate's parent type is left `unknown` —
 * the policy is owner-agnostic. All fields are public observables (no cross-field
 * logic). `value` is nullable while editing (an empty number input); `toSave`
 * coerces an empty value to 0, leaving the server as the validation authority.
 */
export class EditableRetentionPolicy {
  private readonly _owner: RetentionPolicyOwner;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `retention-policy-${(nextRetentionPolicyKey += 1)}`;
  retentionType: RetentionType = DEFAULT_RETENTION_TYPE;
  status: string = DEFAULT_STATUS;
  value: number | null = DEFAULT_VALUE;

  /**
   * @param owner the shared parent→children owner (an `Aggregate`); self-delete routes through it.
   * @param wire when present, seeds the fields from a loaded policy; when absent, the field defaults stand.
   */
  constructor(owner: RetentionPolicyOwner, wire?: RetentionPolicy) {
    this._owner = owner;
    makeObservable(this, {
      retentionType: observable,
      status: observable,
      value: observable,
    });
    if (wire !== undefined) {
      this.status = wire.status;
      this.retentionType = wire.retention_type;
      this.value = wire.value;
    }
  }

  /** Remove this policy from its owner. */
  delete = (): void => {
    this._owner.remove(this);
  };

  /** Project to the wire shape. Empty value → 0 (the server rejects out-of-range values). */
  toSave(): RetentionPolicy {
    return {
      status: this.status,
      retention_type: this.retentionType,
      value: this.value ?? 0,
    };
  }
}
